import React from 'react';
import {
  Award,
  BookOpen,
  Car,
  CheckCircle2,
  ChevronRight,
  Clock,
  Flame,
  Gift,
  Mail,
  Percent,
  Star,
  TriangleAlert,
  Zap,
  LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  resolveBannerVisual,
  visualBackground,
  BannerPreset,
  BannerCustomStyle,
  LocalizedText,
} from '../../../api/banners';

/** Те же иконки, что вшиты в мобильный клиент. */
const ICONS: Record<string, LucideIcon> = {
  gift: Gift,
  clock: Clock,
  flame: Flame,
  zap: Zap,
  star: Star,
  check: CheckCircle2,
  alert: TriangleAlert,
  book: BookOpen,
  car: Car,
  award: Award,
  percent: Percent,
  mail: Mail,
};

interface BannerPreviewProps {
  preset: BannerPreset;
  icon: string;
  title: LocalizedText;
  subtitle?: LocalizedText;
  /** Показывать шеврон — его нет у баннера без действия. */
  showChevron: boolean;
  /** Ручные цвета поверх пресета. */
  custom?: BannerCustomStyle | null;
  lang: 'uz' | 'ru' | 'kiril';
  dark: boolean;
}

/**
 * Как баннер выглядит на главном экране приложения. Повторяет вёрстку
 * mobile/src/components/banners/HomeBanner.tsx: скругление 20, иконка 44×44
 * в квадрате со скруглением 14, заголовок 15/700, подзаголовок 13.
 */
export function BannerPreview({
  preset,
  icon,
  title,
  subtitle,
  showChevron,
  custom,
  lang,
  dark,
}: BannerPreviewProps) {
  const visual = resolveBannerVisual(preset, dark, custom);
  const Icon = ICONS[icon] ?? Star;

  const titleText = title?.[lang] || title?.uz || title?.ru || title?.kiril || 'Заголовок';
  const subtitleText = subtitle?.[lang] || subtitle?.uz || subtitle?.ru || subtitle?.kiril || '';

  return (
    // Фон «экрана приложения»: токен bg; для тёмного превью — в области .dark.
    // Цвета самого баннера — данные пресета, их не трогаем.
    <div className={cn('rounded-2xl bg-bg p-4', dark && 'dark')}>
      <div
        className="flex items-center gap-3.5 rounded-[20px] px-4 py-3.5"
        style={{
          background: visualBackground(visual),
          border: visual.border ? `1px solid ${visual.border}` : undefined,
        }}
      >
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px]"
          style={{ background: visual.iconBg }}
        >
          <Icon className="h-[22px] w-[22px]" style={{ color: visual.iconFg }} />
        </div>

        <div className="min-w-0 flex-1">
          <div
            className="truncate text-[15px] font-bold leading-snug"
            style={{ color: visual.fg }}
          >
            {titleText}
          </div>
          {!!subtitleText && (
            <div className="truncate text-[13px] leading-[18px]" style={{ color: visual.muted }}>
              {subtitleText}
            </div>
          )}
        </div>

        {showChevron && <ChevronRight className="h-5 w-5 shrink-0" style={{ color: visual.muted }} />}
      </div>
    </div>
  );
}
